import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import {
  checkResellerChipAccessQuery,
  assignExistingPetToChipMutation,
} from '@/appview/RegisterChipV2/RegisterChip.graphql';
import {
  reassignChipToResellerMutation,
  recallChipMutation,
} from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import styles from './AddChipToPetModal.module.scss';

interface PetInfo {
  id: string;
  name: string;
  breed?: { name: string } | null;
}

interface AddChipToPetModalProps {
  open: boolean;
  onClose: () => void;
  pet: PetInfo | null;
}

export default function AddChipToPetModal({ open, onClose, pet }: AddChipToPetModalProps) {
  const { showToast } = useToast();
  const [serialNumber, setSerialNumber] = useState('');
  const [serialError, setSerialError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [checkChipAccess] = useLazyQuery(checkResellerChipAccessQuery);
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, {
    refetchQueries: 'active' as const,
  });
  const [recallChip] = useMutation(recallChipMutation, {
    refetchQueries: 'active' as const,
  });
  const [assignExistingPet] = useMutation(assignExistingPetToChipMutation, {
    refetchQueries: 'active' as const,
  });

  function handleClose() {
    setSerialNumber('');
    setSerialError('');
    setSubmitting(false);
    onClose();
  }

  async function handleSubmit() {
    if (!pet) {
      return;
    }
    // Serial Number is only digits, so doesn't need case insensitivity.
    const trimmed = serialNumber.trim();
    if (!trimmed) {
      setSerialError('Serial number is required');
      return;
    }
    setSubmitting(true);
    setSerialError('');
    try {
      // Step 1: Validate chip and determine ownership
      const { data, error } = await checkChipAccess({ variables: { id: trimmed } });
      if (error || !data?.resellerChip) {
        setSerialError('Chip not found in your inventory.');
        return;
      }
      const graphqlId = data.resellerChip.id;
      const chipResellerId = data.resellerChip.reseller?.id;
      const userResellerId = data.currentUser?.chipReseller?.id;
      const needsClaim = !chipResellerId || chipResellerId !== userResellerId;

      // Step 2: Claim the chip if it belongs to another reseller
      let chipId = graphqlId;
      if (needsClaim) {
        const { data: claimData } = await reassignToReseller({
          variables: { input: { serialNumber: data.resellerChip.serialNumber, clearOwnerInfo: false } },
        });
        const claimedId = claimData?.reassignChipToReseller?.id;
        if (claimedId) {
          chipId = claimedId;
          await recallChip({ variables: { input: { chipId: claimedId } } });
        }
      }

      // Step 3: Assign existing pet to the chip
      await assignExistingPet({ variables: { input: { chipId, petId: pet.id } } });
      showToast(`Chip ${trimmed} added to ${pet.name}`);
      handleClose();
    } catch (err) {
      setSerialError(err instanceof Error ? err.message : 'Failed to add chip');
    } finally {
      setSubmitting(false);
    }
  }

  if (!pet) {
    return null;
  }

  const canSubmit = serialNumber.trim().length > 0 && !submitting;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Add Chip to {pet.name}</ModalTitle>
      {pet.breed?.name && <p className={styles.subtitle}>{pet.breed.name}</p>}

      <PortalField label="Chip Serial Number" error={serialError} showError={!!serialError}>
        <PortalInput
          value={serialNumber}
          onChange={(e) => {
            setSerialNumber(e.target.value);
            setSerialError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
          placeholder="e.g. 985112345678900"
          hasError={!!serialError}
          autoFocus
        />
      </PortalField>

      <ModalActions>
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Adding...' : `Add Chip to ${pet.name}`}
        </Button>
      </ModalActions>
    </Modal>
  );
}
